import { useEffect, useState } from 'react'
import { emitterProps } from '../helpers/emitter'
import { openModal } from '../helpers/useModal'
import { type_GM_STATE } from '../../helpers'
import { HeartIcon } from '../icons/HeartIcon'
import { wishlistProviderService } from '../helpers/provider.service'
import { OmneoList } from '../types'

type props = {
  productId: number
  productHandle: string | null
  productTemplate: string | null
}

declare var GM_STATE: type_GM_STATE

export function WishlistItem({ productId, productHandle, productTemplate }: props) {
  const { redirect, ignore } = GM_STATE.wishlist.productTemplates
  const [currentList, setCurrentList] = useState<OmneoList | null>(null)

  if (!productId || !productHandle) {
    return null
  }

  const isRedirectTemplate = productTemplate && redirect.includes(productTemplate)
  const isIgnoreTemplate = productTemplate && ignore.includes(productTemplate)
  if (isIgnoreTemplate) return null

  const wishlistSlideoutEl = document.querySelector('.js-wishlist-product-slideout')
  const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash

  const openAccountSlideout = () => {
    sessionStorage.setItem('returnTo', `/products/${productHandle}`)
    openModal('account-slideout')
  }

  const handleClick = async () => {
    if (isRedirectTemplate) {
      location.href = `${rootUrlWithoutSlash}/products/${productHandle}`
    } else {
      const loggedIn = await wishlistProviderService.isLoggedIn()

      if (!loggedIn) {
        openAccountSlideout()
        return
      }

      openSlideout()
    }
  }

  const openSlideout = () => {
    wishlistSlideoutEl?.setAttribute('product-id', productId.toString())
    wishlistSlideoutEl?.setAttribute('product-handle', productHandle)
    productTemplate && wishlistSlideoutEl?.setAttribute('product-template', productTemplate)
    wishlistSlideoutEl?.setAttribute('open-source', 'plp')
    openModal('wishlist-product-slideout')
  }

  useEffect(() => {
    let isUnmounted = false

    const syncCurrentList = async (lists?: OmneoList[]) => {
      if (!GM_STATE.wishlist.enable) {
        if (!isUnmounted) {
          setCurrentList(null)
        }
        return
      }

      let nextLists = lists

      if (!nextLists) {
        if (!wishlistProviderService.isReady()) {
          return
        }

        const canLoadLists = wishlistProviderService.isCustomerLoggedIn()

        if (!canLoadLists) {
          if (!isUnmounted) {
            wishlistProviderService.clearCachedLists()
            setCurrentList(null)
          }
          return
        }

        nextLists = await wishlistProviderService.getLists()
      }

      const itemList = nextLists.find((list) => list.items.some((item) => item?.meta?.handle === productHandle)) || null

      if (!isUnmounted) {
        setCurrentList(itemList)
      }
    }

    const handleFetchWishlist = (event: Event) => {
      syncCurrentList((event as CustomEvent<OmneoList[]>).detail).then()
    }

    const handleWishlistChange = () => {
      syncCurrentList().then()
    }

    const cachedLists = wishlistProviderService.getCachedLists()
    if (cachedLists.length > 0) {
      syncCurrentList(cachedLists).then()
    }

    if (wishlistProviderService.isReady()) {
      handleWishlistChange()
    }

    const detachReady = wishlistProviderService.onReady(handleWishlistChange)
    const detachProviderEvents = wishlistProviderService.subscribeToListChanges(handleWishlistChange)

    document.addEventListener('fetchWishlist', handleFetchWishlist)
    document.addEventListener(emitterProps.wishlist.add, handleWishlistChange)
    document.addEventListener(emitterProps.wishlist.remove, handleWishlistChange)

    return () => {
      isUnmounted = true
      detachReady()
      detachProviderEvents()
      document.removeEventListener('fetchWishlist', handleFetchWishlist)
      document.removeEventListener(emitterProps.wishlist.add, handleWishlistChange)
      document.removeEventListener(emitterProps.wishlist.remove, handleWishlistChange)
    }
  }, [productHandle])

  useEffect(() => {
    if (!wishlistProviderService.isCustomerLoggedIn()) {
      wishlistProviderService.clearCachedLists()
      setCurrentList(null)
      return
    }

    if (!currentList) {
      const cachedLists = wishlistProviderService.getCachedLists()

      if (cachedLists.length > 0) {
        const itemList = cachedLists.find((list: any) => list.items.some((item: any) => item?.meta?.handle === productHandle))
        setCurrentList(itemList || null)
      }
    }
  }, [currentList, productHandle])

  return (
    <button className="wishlist_icon" type="button" onClick={handleClick}>
      <HeartIcon className={currentList ? 'fill-ink' : 'text-subdued'} />
    </button>
  )
}
